import React, { useEffect, useRef, useState } from 'react';
import { NodeProps, NodeResizer } from 'reactflow';
import type { BoardDrawing, BoardImageData } from '../../../types';
import { useBoardActions } from '../boardActions';
import NodeActionsToolbar from './NodeActionsToolbar';
import { DrawingsSvg } from '../drawingsSvg';
import { normalizeImageOpacity, normalizeImageRotation } from '../drawUtils';
import { useBoardMultiSelected } from '../useBoardSelection';

const ImageNode: React.FC<NodeProps<BoardImageData>> = ({ id, data, selected }) => {
  const { updateNodeData, readOnly, drawTool, selectedDrawing, startDrawingEdit } = useBoardActions();
  const multiSelected = useBoardMultiSelected();
  const [draftCaption, setDraftCaption] = useState(data.caption || '');
  const [editingCallout, setEditingCallout] = useState<BoardDrawing | null>(null);
  const captionRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraftCaption(data.caption || '');
  }, [data.caption]);

  const hasCaption = !!data.caption;
  const showCaptionPrompt = !!(selected && !readOnly && !hasCaption && !multiSelected);
  const drawings = data.drawings || [];
  const overlayDrawings = drawings.filter((d) => d.kind !== 'callout');

  const commitCaption = () => {
    const next = draftCaption.trim();
    if (next !== (data.caption || '')) {
      updateNodeData(id, { caption: next || undefined });
    }
  };

  const commitCallout = (text: string) => {
    if (!editingCallout) return;
    const next = drawings.map((d) =>
      d.id === editingCallout.id ? { ...d, text: text.trim() || undefined } : d,
    );
    updateNodeData(id, { drawings: next });
    setEditingCallout(null);
  };

  const rotation = normalizeImageRotation(data.rotation);
  const opacity = normalizeImageOpacity(data.opacity);
  const quarter = rotation === 90 || rotation === 270;
  const aspect =
    data.naturalWidth && data.naturalHeight
      ? quarter
        ? data.naturalHeight / data.naturalWidth
        : data.naturalWidth / data.naturalHeight
      : undefined;

  return (
    <>
      <NodeResizer
        isVisible={!!selected && !readOnly && drawTool === 'select'}
        minWidth={60}
        minHeight={60}
        keepAspectRatio={!!aspect}
        lineClassName="board-resize-line"
        handleClassName="board-resize-handle"
      />
      <NodeActionsToolbar id={id} showRotate showOpacity opacity={opacity} />
      <div
        className={`board-node board-image-node${selected ? ' is-selected' : ''}${
          opacity < 1 ? ' is-faded' : ''
        }`}
      >
        <div
          className={`board-image-media${quarter ? ' is-quarter-rot' : ''}`}
          style={{ ['--board-image-rot' as string]: `${rotation}deg` }}
        >
          {data.url ? (
            <img src={data.url} alt={data.alt || ''} draggable={false} style={{ opacity }} />
          ) : (
            <div className="board-image-placeholder" style={{ opacity }}>
              Uploading…
            </div>
          )}
          {overlayDrawings.length > 0 && (
            <DrawingsSvg
              className="board-image-drawings"
              drawings={overlayDrawings}
              normalized
              markerId={`board-draw-arrow-${id}`}
              selectedId={selectedDrawing?.target === id ? selectedDrawing.id : null}
              selectable={!readOnly && drawTool === 'select'}
              onSelect={(d, e) => {
                e.preventDefault();
                startDrawingEdit(d, id, e);
              }}
              onCalloutDoubleClick={(d) => {
                if (!readOnly && drawTool === 'select') setEditingCallout(d);
              }}
            />
          )}
          {editingCallout && (
            <input
              autoFocus
              className="board-image-callout-input nodrag nowheel"
              defaultValue={editingCallout.text || ''}
              placeholder="Add a label…"
              onBlur={(e) => commitCallout(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitCallout((e.target as HTMLInputElement).value);
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setEditingCallout(null);
                }
              }}
            />
          )}
        </div>
        {hasCaption && (
          <div className="board-image-caption-wrap nodrag">
            {selected && !readOnly ? (
              <input
                ref={captionRef}
                type="text"
                className="board-image-caption-input nowheel"
                placeholder="Add caption…"
                value={draftCaption}
                onChange={(e) => setDraftCaption(e.target.value)}
                onBlur={commitCaption}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitCaption();
                    (e.target as HTMLInputElement).blur();
                  }
                  if (e.key === 'Escape') {
                    setDraftCaption(data.caption || '');
                    (e.target as HTMLInputElement).blur();
                  }
                }}
              />
            ) : (
              <p className="board-image-caption">{data.caption}</p>
            )}
          </div>
        )}
        {showCaptionPrompt && (
          <div className="board-image-caption-overlay nodrag">
            <input
              ref={captionRef}
              type="text"
              className="board-image-caption-input nowheel"
              placeholder="Add caption…"
              value={draftCaption}
              onChange={(e) => setDraftCaption(e.target.value)}
              onBlur={commitCaption}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  commitCaption();
                  (e.target as HTMLInputElement).blur();
                }
                if (e.key === 'Escape') {
                  setDraftCaption('');
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(ImageNode);
